import React, { useState, useEffect } from 'react';
import client from '../../api/client';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Chip,
    Menu,
    MenuItem,
    Checkbox,
    Tooltip,
    Badge,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    FormControl,
    InputLabel,
    Drawer,
    ListItemButton,
    ListItemIcon,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import {
    Inbox as InboxIcon,
    Send as SendIcon,
    Drafts as DraftsIcon,
    Delete as DeleteIcon,
    Archive as ArchiveIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Reply as ReplyIcon,
    ReplyAll as ReplyAllIcon,
    Forward as ForwardIcon,
    MoreVert as MoreVertIcon,
    AttachFile as AttachFileIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    Label as LabelIcon,
    Folder as FolderIcon,
    Schedule as ScheduleIcon,
    Flag as FlagIcon,
    Print as PrintIcon,
    GetApp as DownloadIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    PersonAdd as PersonAddIcon,
    FormatBold as FormatBoldIcon,
    FormatItalic as FormatItalicIcon,
    FormatUnderlined as FormatUnderlinedIcon,
    FormatListBulleted as FormatListBulletedIcon,
    FormatListNumbered as FormatListNumberedIcon,
    Link as LinkIcon,
    Image as ImageIcon,
    EmojiEmotions as EmojiIcon,
    Add as AddIcon,
    AccessTime as SnoozeIcon,
    Report as SpamIcon,
    LabelImportant as ImportantIcon,
    Settings as SettingsIcon,
    CheckCircle as CheckCircleIcon,
    RotateRight as RotateRightIcon,
    Error as ErrorIcon,
    DoneAll as DoneAllIcon,
} from '@mui/icons-material';

const MailPage = () => {
    const { user: authUser } = useAuth();
    const [selectedFolder, setSelectedFolder] = useState('inbox');
    const [selectedMail, setSelectedMail] = useState(null);
    const [selectedMails, setSelectedMails] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [composeOpen, setComposeOpen] = useState(false);
    const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
    const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
    const [composeData, setComposeData] = useState({
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        attachments: [],
    });
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);

    // Settings State
    const [isConfigured, setIsConfigured] = useState(false);
    const [settingsData, setSettingsData] = useState({
        email: '',
        smtpHost: '',
        imapHost: ''
    });

    // Removed handleSaveSettings as per user request (users cannot change config)

    const fetchConfig = async () => {
        try {
            const res = await client.get('/mail/config');
            if (res.success && res.data && res.verified) {
                setIsConfigured(true);
                setSettingsData({
                    email: res.data.email_address,
                    smtpHost: res.data.smtp_host,
                    imapHost: res.data.imap_host
                });
                // Once config is fetched, sync mails
                fetchMails(true);
            } else {
                setIsConfigured(false);
            }
        } catch (err) {
            console.error('Error fetching email config:', err);
            setIsConfigured(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);


    // Mock mail data
    const [mails, setMails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    const fetchMails = async (forceSync = false) => {
        try {
            setLoading(true);
            const res = await client.get(`/mail?folder=${selectedFolder}&search=${searchQuery}${forceSync ? '&sync=true' : ''}`);
            if (res.success) {

                const formattedMails = res.data.map(m => {
                    // Strip HTML tags for preview
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = m.body || '';
                    const plainText = tempDiv.textContent || tempDiv.innerText || '';

                    return {
                        id: m.id,
                        from: m.sender_name || m.sender_email.split('@')[0],
                        email: m.sender_email,
                        subject: m.subject || '(No Subject)',
                        preview: plainText.substring(0, 100) + (plainText.length > 100 ? '...' : ''),
                        body: m.body || '',
                        date: m.created_at,
                        read: !!m.is_read,
                        starred: !!m.is_starred,
                        folder: m.folder,
                        status: m.status || 'sent',
                        labels: [],
                        attachments: [],
                        flagged: false,
                    };
                });
                setMails(formattedMails);
            }
        } catch (error) {
            console.error('Error fetching mails:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isConfigured) {
            fetchMails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolder, searchQuery]);

    const folders = [
        { id: 'inbox', name: 'Inbox', icon: <InboxIcon fontSize="small" />, count: mails.filter(m => m.folder === 'inbox' && !m.read).length },
        { id: 'starred', name: 'Starred', icon: <StarIcon fontSize="small" />, count: 0 },
        { id: 'snoozed', name: 'Snooze', icon: <SnoozeIcon fontSize="small" />, count: 0 },
        { id: 'sent', name: 'Sent', icon: <SendIcon fontSize="small" />, count: 0 },
        { id: 'drafts', name: 'Drafts', icon: <DraftsIcon fontSize="small" />, count: 0 },
        { id: 'spam', name: 'Spam', icon: <SpamIcon fontSize="small" />, count: 0 },
        { id: 'trash', name: 'Trash', icon: <DeleteIcon fontSize="small" />, count: 0 },
    ];

    const filteredMails = mails; // Filtering handled by backend now

    const handleMailClick = async (mail) => {
        setSelectedMail(mail);
        if (!mail.read) {
            try {
                await client.put(`/mail/${mail.id}`, { is_read: true });
                setMails(mails.map(m => m.id === mail.id ? { ...m, read: true } : m));
            } catch (err) {
                console.error('Error marking read:', err);
            }
        }
    };

    const handleStarToggle = async (mailId, e) => {
        e.stopPropagation();
        const mail = mails.find(m => m.id === mailId);
        if (!mail) return;

        try {
            await client.put(`/mail/${mailId}`, { is_starred: !mail.starred });
            setMails(mails.map(m => m.id === mailId ? { ...m, starred: !m.starred } : m));
        } catch (err) {
            console.error('Error toggling star:', err);
        }
    };

    const handleSelectMail = (mailId, e) => {
        e.stopPropagation();
        if (selectedMails.includes(mailId)) {
            setSelectedMails(selectedMails.filter(id => id !== mailId));
        } else {
            setSelectedMails([...selectedMails, mailId]);
        }
    };

    const handleDeleteSelected = async () => {
        try {
            await Promise.all(selectedMails.map(id => client.delete(`/mail/${id}`)));
            // Refresh logic - optimistic update
            setMails(mails.filter(m => !selectedMails.includes(m.id)));
            setSelectedMails([]);
            if (selectedMail && selectedMails.includes(selectedMail.id)) {
                setSelectedMail(null);
            }
        } catch (err) {
            console.error('Error deleting mails:', err);
        }
    };

    const handleArchiveSelected = async () => {
        try {
            await Promise.all(selectedMails.map(id => client.put(`/mail/${id}`, { folder: 'archive' })));
            setMails(mails.filter(m => !selectedMails.includes(m.id)));
            setSelectedMails([]);
        } catch (err) {
            console.error('Error archiving mails:', err);
        }
    };

    const handleCompose = () => {
        setComposeOpen(true);
        setComposeData({
            to: '',
            cc: '',
            bcc: '',
            subject: '',
            body: '',
            attachments: [],
        });
    };

    const handleReply = () => {
        if (selectedMail) {
            setComposeOpen(true);
            setComposeData({
                to: selectedMail.email,
                cc: '',
                bcc: '',
                subject: selectedMail.subject.startsWith('Re:') ? selectedMail.subject : `Re: ${selectedMail.subject}`,
                body: `\n\n---\nOn ${new Date(selectedMail.date).toLocaleString()}, ${selectedMail.from} wrote:\n${selectedMail.body}`,
                attachments: [],
            });
        }
    };

    const handleForward = () => {
        if (selectedMail) {
            setComposeOpen(true);
            setComposeData({
                to: '',
                cc: '',
                bcc: '',
                subject: selectedMail.subject.startsWith('Fwd:') ? selectedMail.subject : `Fwd: ${selectedMail.subject}`,
                body: `\n\n---\nForwarded message from ${selectedMail.from}:\n${selectedMail.body}`,
                attachments: selectedMail.attachments,
            });
        }
    };

    const handleSendMail = async () => {
        if (!composeData.to) return alert('Please enter a recipient');

        try {
            setSending(true);
            await client.post('/mail/send', {
                recipientEmail: composeData.to,
                subject: composeData.subject,
                body: composeData.body
            });
            setComposeOpen(false);
            setComposeData({
                to: '',
                cc: '',
                bcc: '',
                subject: '',
                body: '',
                attachments: [],
            });

            // Refresh if in Sent folder
            if (selectedFolder === 'sent') fetchMails();
            alert('Email sent successfully!');
        } catch (err) {
            console.error('Error sending email:', err);
            alert('Failed to send email. Check your SMTP configuration.');
        } finally {
            setSending(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f6f8fc' }}>
            {/* Sidebar */}
            <Box
                sx={{
                    width: 256,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2,
                    pt: 1,
                    borderRight: '1px solid #e0e0e0',
                }}
            >
                {/* User Info Section */}
                <Box sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: '12px' }}>
                    <Avatar sx={{ bgcolor: '#0b57d0', width: 40, height: 40 }}>
                        {authUser?.name ? authUser.name[0].toUpperCase() : 'U'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {authUser?.name || 'User'}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {settingsData.email || 'Not configured'}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    onClick={handleCompose}
                    startIcon={<AddIcon sx={{ fontSize: 24, color: '#34a853' }} />}
                    sx={{
                        width: 'fit-content',
                        minWidth: 140,
                        height: 56,
                        px: 3,
                        mb: 2,
                        borderRadius: '16px',
                        bgcolor: '#c2e7ff',
                        color: '#001d35',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#b3d7ef',
                            boxShadow: '0 1px 3px 0 rgba(60,64,67,.3), 0 4px 8px 3px rgba(60,64,67,.15)',
                        },
                    }}
                >
                    Compose
                </Button>

                <List sx={{ px: 0 }}>
                    {folders.map((folder) => (
                        <ListItemButton
                            key={folder.id}
                            selected={selectedFolder === folder.id}
                            onClick={() => setSelectedFolder(folder.id)}
                            sx={{
                                borderRadius: '0 24px 24px 0',
                                mb: 0.5,
                                pr: 3,
                                '&.Mui-selected': {
                                    bgcolor: '#d3e3fd',
                                    color: '#041e49',
                                    '&:hover': { bgcolor: '#c2d7f7' },
                                    '& .MuiListItemIcon-root': { color: '#041e49' },
                                    '& .MuiTypography-root': { fontWeight: 700 },
                                },
                                '&:hover': {
                                    bgcolor: selectedFolder === folder.id ? '#c2d7f7' : 'rgba(0,0,0,0.04)',
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40, color: selectedFolder === folder.id ? '#041e49' : '#444746' }}>
                                {folder.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={folder.name}
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                            />
                            {folder.count > 0 && (
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                    {folder.count}
                                </Typography>
                            )}
                        </ListItemButton>
                    ))}
                </List>

                <Box sx={{ mt: 'auto', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: isConfigured ? '#34a853' : '#ea4335' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {isConfigured ? 'Connected' : 'Offline'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Middle Panel - Mail List */}
            <Paper
                elevation={0}
                sx={{
                    width: 440,
                    borderRight: '1px solid #e0e0e0',
                    bgcolor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px 16px 0 0',
                    ml: 0.5,
                }}
            >
                {/* Toolbar */}
                <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                        size="small"
                        checked={selectedMails.length === filteredMails.length && filteredMails.length > 0}
                        indeterminate={selectedMails.length > 0 && selectedMails.length < filteredMails.length}
                        onChange={(e) => {
                            if (e.target.checked) setSelectedMails(filteredMails.map(m => m.id));
                            else setSelectedMails([]);
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchMails(true)}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="More">
                        <IconButton size="small" onClick={(e) => setFilterMenuAnchor(e.currentTarget)}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {selectedMails.length > 0 && (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />
                            <Tooltip title="Archive">
                                <IconButton size="small" onClick={handleArchiveSelected}>
                                    <ArchiveIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small" onClick={handleDeleteSelected}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search in mail"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 36,
                                    bgcolor: '#f1f3f4',
                                    '& fieldset': { border: 'none' },
                                    borderRadius: '8px',
                                },
                                width: 140,
                            }}
                        />
                    </Box>
                </Box>

                {/* Mail List */}
                <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}><RotateRightIcon className="spinning" /></Box>
                    ) : filteredMails.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No mail found</Box>
                    ) : (
                        filteredMails.map((mail) => (
                            <ListItemButton
                                key={mail.id}
                                selected={selectedMail?.id === mail.id}
                                onClick={() => handleMailClick(mail)}
                                sx={{
                                    borderBottom: '1px solid #f2f2f2',
                                    bgcolor: mail.read ? 'white' : 'rgba(0,0,0,0.02)',
                                    py: 1.25,
                                    px: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&.Mui-selected': {
                                        bgcolor: '#e8f0fe',
                                        '&:hover': { bgcolor: '#d2e3fc' },
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                    <Checkbox
                                        size="small"
                                        checked={selectedMails.includes(mail.id)}
                                        onChange={(e) => handleSelectMail(mail.id, e)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleStarToggle(mail.id, e)}
                                        sx={{ p: 0.5, mr: 1 }}
                                    >
                                        {mail.starred ? (
                                            <StarIcon fontSize="small" sx={{ color: '#f4b400' }} />
                                        ) : (
                                            <StarBorderIcon fontSize="small" sx={{ color: '#5f6368' }} />
                                        )}
                                    </IconButton>

                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: mail.read ? 400 : 700,
                                                    color: mail.read ? '#5f6368' : '#202124',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {mail.from}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#5f6368', ml: 1 }}>
                                                {formatDate(mail.date)}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: mail.read ? 400 : 700,
                                                color: '#202124',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {mail.subject}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#5f6368',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flex: 1
                                                }}
                                            >
                                                {mail.preview}
                                            </Typography>
                                            {mail.status && mail.folder === 'sent' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {mail.status === 'sent' && <CheckCircleIcon sx={{ fontSize: 14, color: '#188038' }} />}
                                                    {mail.status === 'pending' && <RotateRightIcon sx={{ fontSize: 14, color: '#f29900', animation: 'spin 2s linear infinite' }} />}
                                                    {mail.status === 'failed' && <ErrorIcon sx={{ fontSize: 14, color: '#d93025' }} />}
                                                    {mail.status === 'delivered' && <DoneAllIcon sx={{ fontSize: 14, color: '#1a73e8' }} />}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </ListItemButton>
                        ))
                    )}
                </List>
            </Paper>

            {/* Right Panel - Mail Content */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'white',
                    borderRadius: '16px 16px 0 0',
                    mr: 1,
                    ml: 1,
                }}
            >
                {selectedMail ? (
                    <>
                        {/* Mail View Toolbar */}
                        <Box sx={{ p: 1, borderBottom: '1px solid #f2f2f2', display: 'flex', gap: 1 }}>
                            <Tooltip title="Back">
                                <IconButton size="small" onClick={() => setSelectedMail(null)}>
                                    <ForwardIcon sx={{ transform: 'rotate(180deg)', fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Archive">
                                <IconButton size="small">
                                    <ArchiveIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Report spam">
                                <IconButton size="small">
                                    <SpamIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />
                            <Tooltip title="Mark as unread">
                                <IconButton size="small">
                                    <DraftsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Snooze">
                                <IconButton size="small">
                                    <SnoozeIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 400, color: '#202124', mr: 1 }}>
                                        {selectedMail.subject}
                                    </Typography>
                                    <Chip label="Inbox" size="small" sx={{ height: 20, fontSize: '0.75rem', borderRadius: '4px' }} />
                                </Box>
                                <Box>
                                    <IconButton size="small"><PrintIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><DownloadIcon fontSize="small" /></IconButton>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ mr: 2, bgcolor: '#1a73e8', width: 40, height: 40, fontSize: '1rem' }}>
                                    {selectedMail.from[0].toUpperCase()}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {selectedMail.from}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            &lt;{selectedMail.email}&gt;
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        to me
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(selectedMail.date).toLocaleString([], { dateStyle: 'long', timeStyle: 'short' })}
                                    </Typography>
                                    <Box>
                                        <IconButton size="small" onClick={handleReply}><ReplyIcon sx={{ fontSize: 18 }} /></IconButton>
                                        <IconButton size="small"><MoreVertIcon sx={{ fontSize: 18 }} /></IconButton>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    lineHeight: 1.6,
                                    color: '#3c4043',
                                    fontSize: '0.875rem',
                                    '& img': { maxWidth: '100%', height: 'auto' },
                                    '& p': { mb: 1.5 },
                                    '& a': { color: '#1a73e8' }
                                }}
                                dangerouslySetInnerHTML={{ __html: selectedMail.body }}
                            />

                            <Box sx={{ mt: 4, display: 'flex', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ReplyIcon />}
                                    onClick={handleReply}
                                    sx={{ borderRadius: '24px', textTransform: 'none', px: 3, borderColor: '#dadce0', color: '#5f6368' }}
                                >
                                    Reply
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ForwardIcon />}
                                    onClick={handleForward}
                                    sx={{ borderRadius: '24px', textTransform: 'none', px: 3, borderColor: '#dadce0', color: '#5f6368' }}
                                >
                                    Forward
                                </Button>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', bgcolor: '#f6f8fc' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <InboxIcon sx={{ fontSize: 80, mb: 2, opacity: 0.1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 400 }}>Select an item to read</Typography>
                            <Typography variant="body2">Nothing is selected</Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Compose Dialog */}
            <Dialog
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { height: '80vh', borderRadius: '12px' },
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #f2f2f2', py: 1.5, bgcolor: '#f2f6fc' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>New Message</Typography>
                        <IconButton onClick={() => setComposeOpen(false)} size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ px: 2, pt: 1 }}>
                        <TextField
                            fullWidth
                            variant="standard"
                            placeholder="Recipients"
                            value={composeData.to}
                            onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                            sx={{ mb: 1, '& .MuiInput-underline:before': { borderBottomColor: '#f2f2f2' } }}
                            InputProps={{
                                startAdornment: <Typography variant="body2" sx={{ mr: 1, color: '#5f6368' }}>To</Typography>,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button size="small" onClick={() => setShowCc(!showCc)} sx={{ textTransform: 'none', color: '#5f6368', minWidth: 'auto', mr: 1 }}>Cc</Button>
                                        <Button size="small" onClick={() => setShowBcc(!showBcc)} sx={{ textTransform: 'none', color: '#5f6368', minWidth: 'auto' }}>Bcc</Button>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {showCc && (
                            <TextField
                                fullWidth
                                variant="standard"
                                placeholder="Cc"
                                value={composeData.cc}
                                onChange={(e) => setComposeData({ ...composeData, cc: e.target.value })}
                                sx={{ mb: 1, '& .MuiInput-underline:before': { borderBottomColor: '#f2f2f2' } }}
                                InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 1, color: '#5f6368' }}>Cc</Typography> }}
                            />
                        )}
                        {showBcc && (
                            <TextField
                                fullWidth
                                variant="standard"
                                placeholder="Bcc"
                                value={composeData.bcc}
                                onChange={(e) => setComposeData({ ...composeData, bcc: e.target.value })}
                                sx={{ mb: 1, '& .MuiInput-underline:before': { borderBottomColor: '#f2f2f2' } }}
                                InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 1, color: '#5f6368' }}>Bcc</Typography> }}
                            />
                        )}
                        <TextField
                            fullWidth
                            variant="standard"
                            placeholder="Subject"
                            value={composeData.subject}
                            onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                            sx={{ mb: 1, '& .MuiInput-underline:before': { borderBottomColor: '#f2f2f2' } }}
                        />
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={16}
                        placeholder="Body"
                        value={composeData.body}
                        onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                        sx={{
                            px: 2,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: 'none' },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'flex-start', borderTop: '1px solid #f2f2f2' }}>
                    <Button
                        variant="contained"
                        onClick={handleSendMail}
                        disabled={sending}
                        sx={{
                            bgcolor: '#0b57d0',
                            borderRadius: '24px',
                            px: 4,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#0842a0', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#0b57d0', opacity: 0.6, color: 'white' }
                        }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>

                    <Box sx={{ ml: 1, display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Formatting options"><IconButton size="small"><FormatBoldIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Attach files"><IconButton size="small"><AttachFileIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Insert link"><IconButton size="small"><LinkIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Insert emoji"><IconButton size="small"><EmojiIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>

                    <Box sx={{ ml: 'auto' }}>
                        <Tooltip title="Discard draft">
                            <IconButton onClick={() => setComposeOpen(false)} size="small">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* More Menu */}
            <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
            >
                <MenuItem onClick={() => setMoreMenuAnchor(null)}>
                    <PrintIcon fontSize="small" sx={{ mr: 1 }} /> Print
                </MenuItem>
                <MenuItem onClick={() => setMoreMenuAnchor(null)}>
                    <FlagIcon fontSize="small" sx={{ mr: 1 }} /> Flag
                </MenuItem>
                <MenuItem onClick={() => setMoreMenuAnchor(null)}>
                    <LabelIcon fontSize="small" sx={{ mr: 1 }} /> Categorize
                </MenuItem>
                <MenuItem onClick={() => setMoreMenuAnchor(null)}>
                    <ScheduleIcon fontSize="small" sx={{ mr: 1 }} /> Schedule
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => setMoreMenuAnchor(null)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                </MenuItem>
            </Menu>

            {/* Filter Menu */}
            <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={() => setFilterMenuAnchor(null)}
            >
                <MenuItem onClick={() => setFilterMenuAnchor(null)}>All</MenuItem>
                <MenuItem onClick={() => setFilterMenuAnchor(null)}>Unread</MenuItem>
                <MenuItem onClick={() => setFilterMenuAnchor(null)}>Flagged</MenuItem>
                <MenuItem onClick={() => setFilterMenuAnchor(null)}>With Attachments</MenuItem>
            </Menu>

            {/* Removed Settings Dialog as per user request */}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spinning {
                    animation: spin 2s linear infinite;
                }
            `}</style>
        </Box>
    );
};

export default MailPage;
